import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { Button, Tooltip, CircularProgress } from '@mui/material';
import * as XLSX from 'xlsx';
import { fetchAdminTournaments } from "../api/adminViewApi"
import '../TournamentList.css';

// one line, cut with an ellipsis when it does not fit
const ELLIPSIS_STYLE = {
    display: 'block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

// truncated cell content with the full value on hover
const EllipsisCell = ({ title, children }) => (
    <Tooltip title={title || ''}>
        <span style={ELLIPSIS_STYLE}>{children}</span>
    </Tooltip>
);

const PAGE_SIZE = 10;
const TABLE_COLUMNS = 9;
// keeps the table the same height as a full 10-row page while empty/loading
const ROW_HEIGHT = 44;
const EMPTY_TABLE_HEIGHT = PAGE_SIZE * ROW_HEIGHT;

const AdminViewTournaments = () => {
    const [tournaments, setTournaments] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    // starts true so the first paint is the loader, never an empty table
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();

    const getTournaments = async (page = 1) => {
        setLoading(true);
        try {
            const data = await fetchAdminTournaments(page, PAGE_SIZE);
            console.log(data, "data")

            // newest first
            const list = data.data.data || [];
            const sorted = [...list].sort((a, b) => {
                const aTime = new Date(a.created_at || a.createdAt || 0).getTime();
                const bTime = new Date(b.created_at || b.createdAt || 0).getTime();

                if (bTime !== aTime) {
                    return bTime - aTime;
                }
                return (b.id || 0) - (a.id || 0);
            });

            setTournaments(sorted);
            setTotalPages(data.data.totalPages || 1);
            setCurrentPage(data.data.currentPage || page);
        } catch (error) {
            console.error('Error fetching tournaments:', error);
            // fall through to the No Data state instead of stale rows
            setTournaments([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getTournaments(currentPage);
    }, [currentPage]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleBackClick = () => {
        navigate('/dashboard');
    };

    const downloadExcel = () => {
        const rows = tournaments.map(tournament => ({
            "ID": tournament.id,
            "Tournament Code": tournament.unique_code,
            "Description": tournament.description,
            "Format": tournament.format,
            "Max Participants": tournament.max_participants,
            "Participants Count": tournament.participants_count,
            "Match Duration": tournament.match_duration,
            "Start Time": new Date(tournament.start_time).toLocaleString(),
            "State": tournament.state,
            "Trainer Name": tournament.admin?.name || 'N/A',
            "Trainer Email": tournament.admin?.email || 'N/A'
        }));

        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Tournaments");

        XLSX.writeFile(wb, "admin_tournaments.xlsx");
    };

    // 24-hour clock, no AM/PM
    const formatDate = (dateString) => {
        if (!dateString) {
            return 'NA';
        }

        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return 'NA';
        }

        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).replace(',', '');
    };

    return (
        <div style={{ padding: '20px' }}>
            <h1>Tournaments Under My Trainers</h1>
            <Button
                variant="contained"
                color="secondary"
                onClick={handleBackClick}
                sx={{ marginBottom: 2 }}
            >
                Back to Dashboard
            </Button>
            <Button
                variant="contained"
                color="primary"
                onClick={downloadExcel}
                sx={{ marginLeft: 2, marginBottom: 2 }}
            >
                Download Excel
            </Button>

            {/* the table is always rendered so the layout stays identical
                between the loading, empty and populated states */}
            <>
                    {/* table-layout: fixed makes the colgroup widths stick */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px', tableLayout: 'fixed' }}>
                        <colgroup>
                            <col style={{ width: '70px' }} />
                            <col style={{ width: '120px' }} />
                            <col style={{ width: '220px' }} />
                            <col style={{ width: '130px' }} />
                            <col style={{ width: '110px' }} />
                            <col style={{ width: '130px' }} />
                            <col style={{ width: '150px' }} />
                            <col style={{ width: '120px' }} />
                            <col style={{ width: '220px' }} />
                        </colgroup>
                        <thead>
                            <tr>
                                <th>S.No.</th>
                                <th>Code</th>
                                <th>Description</th>
                                <th>Format</th>
                                <th>Max Participants</th>
                                <th>Current Participants</th>
                                <th>Start Time</th>
                                <th>State</th>
                                <th>Trainer</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                // Loader stays until the response lands
                                <tr>
                                    <td
                                        colSpan={TABLE_COLUMNS}
                                        style={{
                                            height: EMPTY_TABLE_HEIGHT,
                                            textAlign: 'center',
                                            verticalAlign: 'middle',
                                        }}
                                    >
                                        <CircularProgress size={40} />
                                    </td>
                                </tr>
                            ) : tournaments.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={TABLE_COLUMNS}
                                        style={{
                                            height: EMPTY_TABLE_HEIGHT,
                                            textAlign: 'center',
                                            verticalAlign: 'middle',
                                            fontWeight: 500,
                                            color: '#666',
                                        }}
                                    >
                                        No Data Available
                                    </td>
                                </tr>
                            ) : (
                            tournaments.map((tournament, index) => {
                                const description = tournament.description || 'NA';
                                const format = tournament.format || 'NA';
                                const trainerName = tournament.admin?.name || 'N/A';
                                const trainerEmail = tournament.admin?.email || 'N/A';
                                const trainer = `${trainerName} (${trainerEmail})`;

                                return (
                                <tr key={index}>
                                    {/* serial number continues across pages: page 2 starts at 11 */}
                                    <td>{(currentPage - 1) * PAGE_SIZE + index + 1}</td>
                                    <td>
                                        <EllipsisCell title={tournament.unique_code}>
                                            {tournament.unique_code}
                                        </EllipsisCell>
                                    </td>
                                    <td>
                                        <EllipsisCell title={description}>{description}</EllipsisCell>
                                    </td>
                                    <td>
                                        <EllipsisCell title={format}>{format}</EllipsisCell>
                                    </td>
                                    <td>{tournament.max_participants}</td>
                                    <td>{tournament.participants_count || 0}</td>
                                    <td>{formatDate(tournament.start_time)}</td>
                                    <td>
                                        <span style={{
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            backgroundColor: 
                                                tournament.state === 'Completed' ? '#4caf50' :
                                                tournament.state === 'In-Progress' ? '#ff9800' :
                                                tournament.state === 'Scheduled' ? '#2196f3' :
                                                '#f44336',
                                            color: 'white',
                                            fontSize: '12px'
                                        }}>
                                            {tournament.state}
                                        </span>
                                    </td>
                                    <td>
                                        {/* name and email on one line */}
                                        <EllipsisCell title={trainer}>
                                            <strong>{trainerName}</strong>
                                            <span style={{ fontSize: '12px', color: '#666' }}>
                                                {' '}({trainerEmail})
                                            </span>
                                        </EllipsisCell>
                                    </td>
                                </tr>
                                );
                            })
                            )}
                        </tbody>
                    </table>

                    {/* hidden while loading and when there is nothing to page through */}
                    {!loading && tournaments.length > 0 && (
                        <div style={{ marginTop: '20px' }}>
                            <button onClick={() => handlePageChange(currentPage - 1)} disabled={loading || currentPage === 1}>
                                Previous
                            </button>
                            <span style={{ margin: '0 10px' }}> Page {currentPage} of {totalPages} </span>
                            <button onClick={() => handlePageChange(currentPage + 1)} disabled={loading || currentPage === totalPages}>
                                Next
                            </button>
                        </div>
                    )}
            </>
        </div>
    );
};

export default AdminViewTournaments;

